import { useState, useEffect } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import LiveStreamPlayer from "@/components/LiveStreamPlayer";
import { Broadcast, VideoCamera, Gear } from "@phosphor-icons/react";

/** Per-club or per-court streaming configuration: choose provider (Synology / EZVIZ / Hikvision / HLS).
 *
 * Props:
 *   clubId: string
 *   courtName: optional — when provided, config is per-court instead of club-level
 *   isAdmin: boolean
 *   compact: boolean — smaller buttons for inline use
 */
export default function ClubStreamingSetup({ clubId, courtName, isAdmin, compact }) {
  const [cfg, setCfg] = useState({ provider: "", hls_url: "", ezviz: { device_serial: "", app_key: "", app_secret: "", verification_code: "" }, synology: { camera_id: "" }, hikvision: { ip: "", port: "554", channel: "1", user: "", password: "", hls_url: "" } });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [openCfg, setOpenCfg] = useState(false);
  const [openLive, setOpenLive] = useState(false);
  const [liveUrl, setLiveUrl] = useState(null);
  const [liveFormat, setLiveFormat] = useState("hls");
  const [liveLoading, setLiveLoading] = useState(false);

  const apiBase = courtName ? `/clubs/${clubId}/streaming?court_name=${encodeURIComponent(courtName)}` : `/clubs/${clubId}/streaming`;

  const load = () => {
    setLoading(true);
    api.get(apiBase)
      .then(({ data }) => {
        setCfg({
          provider: data.provider || "",
          hls_url: data.hls_url || "",
          ezviz: { device_serial: "", app_key: "", app_secret: "", verification_code: "", ...(data.ezviz || {}) },
          synology: { camera_id: "", ...(data.synology || {}) },
          hikvision: { ip: "", port: "554", channel: "1", user: "", password: "", hls_url: "", ...(data.hikvision || {}) },
        });
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  };
  useEffect(() => { if (clubId) load(); }, [clubId, courtName]); // eslint-disable-line

  const save = async () => {
    setSaving(true);
    try {
      const body = courtName ? { ...cfg, court_name: courtName } : cfg;
      await api.put(`/clubs/${clubId}/streaming`, body);
      toast.success(courtName ? `Streaming de ${courtName} actualizado` : "Streaming actualizado");
      setOpenCfg(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    } finally { setSaving(false); }
  };

  const goLive = async () => {
    setOpenLive(true);
    setLiveLoading(true);
    setLiveUrl(null);
    try {
      const url = courtName ? `/clubs/${clubId}/streaming/live-url?court_name=${encodeURIComponent(courtName)}` : `/clubs/${clubId}/streaming/live-url`;
      const { data } = await api.get(url);
      setLiveUrl(data.url);
      setLiveFormat(data.format || "hls");
      if (data.format === "rtsp") {
        toast.warning("Stream RTSP — necesitas un gateway RTSP→HLS para reproducir en navegador");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al obtener stream");
      setLiveUrl(null);
    } finally { setLiveLoading(false); }
  };

  const providerLabel = { synology: "Synology", ezviz: "EZVIZ", hls: "HLS Directo", hikvision: "Hikvision" }[cfg.provider] || "No configurado";
  const tid = courtName ? `streaming-${courtName.replace(/\s+/g, "-")}` : "streaming-club";

  return (
    <div className={compact ? "" : "card-tactical p-5"} data-testid={`${tid}-setup`}>
      <div className={`flex items-center justify-between flex-wrap gap-2 ${compact ? "" : "mb-3"}`}>
        {!compact && (
          <div className="flex items-center gap-2">
            <Broadcast size={18} weight="duotone" className="text-[#CCFF00]" />
            <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#CCFF00]">{courtName ? `Streaming · ${courtName}` : "Streaming en vivo"}</p>
          </div>
        )}
        <div className="flex items-center gap-2 flex-wrap">
          {!compact && <span className="text-[10px] uppercase tracking-widest text-gray-500" data-testid={`${tid}-provider`}>{providerLabel}</span>}
          <Button size="sm" onClick={goLive} disabled={!cfg.provider} className="bg-red-500 hover:bg-red-600 text-white font-bold uppercase text-xs h-8" data-testid={`${tid}-live-btn`}>
            <VideoCamera size={14} className="mr-1" /> Live
          </Button>
          {isAdmin && (
            <Button size="sm" variant="outline" onClick={() => setOpenCfg(true)} className="border-[#CCFF00]/30 text-[#CCFF00] hover:bg-[#CCFF00]/10 uppercase text-xs h-8" data-testid={`${tid}-config-btn`}>
              <Gear size={14} className="mr-1" /> Cámara
            </Button>
          )}
        </div>
      </div>
      {!compact && !loading && (
        <>
          <p className="text-[11px] text-gray-500">
            {cfg.provider === "synology" && "Synology Surveillance — H.264 HLS sobre QuickConnect."}
            {cfg.provider === "ezviz" && "Cámara EZVIZ — HLS via Open Platform."}
            {cfg.provider === "hikvision" && "Cámara Hikvision — RTSP/HLS via ISAPI."}
            {cfg.provider === "hls" && "HLS directo — URL .m3u8 desde cualquier proveedor."}
            {!cfg.provider && "Sin configurar. Pulsa 'Cámara' para conectar."}
          </p>
          <p className="text-[10px] text-[#CCFF00]/70 mt-1 flex items-center gap-1 font-semibold uppercase tracking-wider">
            ⚡ Compresión <span className="font-mono">H.264 / AVC</span> — compatible con todos los navegadores (Safari, Chrome, Edge, Firefox)
          </p>
        </>
      )}

      {/* Live Modal */}
      <Dialog open={openLive} onOpenChange={setOpenLive}>
        <DialogContent className="bg-black border-white/10 max-w-4xl p-2" data-testid={`${tid}-live-dialog`}>
          <DialogHeader><DialogTitle className="text-[#CCFF00] text-sm uppercase tracking-widest">{courtName || "Live"} · {providerLabel}</DialogTitle></DialogHeader>
          {liveLoading ? (
            <div className="aspect-video flex items-center justify-center"><div className="w-8 h-8 border-2 border-[#CCFF00] border-t-transparent animate-spin" /></div>
          ) : (
            <LiveStreamPlayer url={liveUrl} format={liveFormat} className="w-full aspect-video" testId={`${tid}-live-player`} allowScreenshot={true} />
          )}
        </DialogContent>
      </Dialog>

      {/* Config Modal */}
      <Dialog open={openCfg} onOpenChange={setOpenCfg}>
        <DialogContent className="bg-[#141414] border-white/10 max-w-xl max-h-[90vh] overflow-y-auto" data-testid={`${tid}-config-dialog`}>
          <DialogHeader><DialogTitle className="font-['Barlow_Condensed'] text-2xl uppercase">{courtName ? `Configurar cámara · ${courtName}` : "Configurar streaming"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-widest text-[#CCFF00] font-semibold mb-2 block">Proveedor</Label>
              <div className="grid grid-cols-4 gap-2">
                {[
                  { id: "synology", label: "Synology" },
                  { id: "ezviz", label: "EZVIZ" },
                  { id: "hikvision", label: "Hikvision" },
                  { id: "hls", label: "HLS" },
                ].map((p) => (
                  <button key={p.id} type="button"
                    onClick={() => setCfg({ ...cfg, provider: p.id })}
                    className={`px-2 py-2 text-[10px] uppercase tracking-wider font-bold border transition-colors ${cfg.provider === p.id ? "bg-[#CCFF00] text-black border-[#CCFF00]" : "bg-[#0A0A0A] text-gray-400 border-white/10 hover:border-[#CCFF00]/30"}`}
                    data-testid={`${tid}-provider-${p.id}`}>
                    {p.label}
                  </button>
                ))}
              </div>
            </div>

            {cfg.provider === "ezviz" && (
              <div className="space-y-3 bg-[#0A0A0A] border border-white/5 p-3" data-testid={`${tid}-ezviz`}>
                <p className="text-[11px] text-gray-400">Registra tu app en <a href="https://open.ezvizlife.com/" target="_blank" rel="noreferrer" className="text-[#CCFF00] underline">open.ezvizlife.com</a> para AppKey y AppSecret. Serial está en la base de la cámara.</p>
                <div><Label className="text-[10px] uppercase tracking-widest text-gray-400">Device Serial</Label><Input value={cfg.ezviz.device_serial} onChange={(e) => setCfg({ ...cfg, ezviz: { ...cfg.ezviz, device_serial: e.target.value } })} placeholder="C12345678" className="bg-[#141414] border-white/10 text-white h-9 mt-1" /></div>
                <div className="grid grid-cols-2 gap-2">
                  <div><Label className="text-[10px] uppercase tracking-widest text-gray-400">App Key</Label><Input value={cfg.ezviz.app_key} onChange={(e) => setCfg({ ...cfg, ezviz: { ...cfg.ezviz, app_key: e.target.value } })} className="bg-[#141414] border-white/10 text-white h-9 mt-1" /></div>
                  <div><Label className="text-[10px] uppercase tracking-widest text-gray-400">App Secret</Label><Input type="password" value={cfg.ezviz.app_secret} onChange={(e) => setCfg({ ...cfg, ezviz: { ...cfg.ezviz, app_secret: e.target.value } })} className="bg-[#141414] border-white/10 text-white h-9 mt-1" /></div>
                </div>
                <div><Label className="text-[10px] uppercase tracking-widest text-gray-400">Código de verificación</Label><Input value={cfg.ezviz.verification_code} onChange={(e) => setCfg({ ...cfg, ezviz: { ...cfg.ezviz, verification_code: e.target.value } })} placeholder="6 caracteres impresos" className="bg-[#141414] border-white/10 text-white h-9 mt-1" /></div>
              </div>
            )}

            {cfg.provider === "hikvision" && (
              <div className="space-y-3 bg-[#0A0A0A] border border-white/5 p-3" data-testid={`${tid}-hikvision`}>
                <p className="text-[11px] text-gray-400">Si tu Hikvision/NVR genera HLS, pega esa URL abajo. Si no, completa IP/usuario/password/canal y el sistema construye la URL RTSP (necesitarás un gateway RTSP→HLS para reproducir en navegador).</p>
                <div><Label className="text-[10px] uppercase tracking-widest text-gray-400">URL HLS (recomendado)</Label><Input value={cfg.hikvision.hls_url} onChange={(e) => setCfg({ ...cfg, hikvision: { ...cfg.hikvision, hls_url: e.target.value } })} placeholder="https://nvr/.../live.m3u8" className="bg-[#141414] border-white/10 text-white h-9 mt-1" /></div>
                <div className="grid grid-cols-2 gap-2">
                  <div><Label className="text-[10px] uppercase tracking-widest text-gray-400">IP</Label><Input value={cfg.hikvision.ip} onChange={(e) => setCfg({ ...cfg, hikvision: { ...cfg.hikvision, ip: e.target.value } })} placeholder="192.168.1.50" className="bg-[#141414] border-white/10 text-white h-9 mt-1" /></div>
                  <div><Label className="text-[10px] uppercase tracking-widest text-gray-400">Puerto RTSP</Label><Input value={cfg.hikvision.port} onChange={(e) => setCfg({ ...cfg, hikvision: { ...cfg.hikvision, port: e.target.value } })} placeholder="554" className="bg-[#141414] border-white/10 text-white h-9 mt-1" /></div>
                  <div><Label className="text-[10px] uppercase tracking-widest text-gray-400">Usuario</Label><Input value={cfg.hikvision.user} onChange={(e) => setCfg({ ...cfg, hikvision: { ...cfg.hikvision, user: e.target.value } })} className="bg-[#141414] border-white/10 text-white h-9 mt-1" /></div>
                  <div><Label className="text-[10px] uppercase tracking-widest text-gray-400">Password</Label><Input type="password" value={cfg.hikvision.password} onChange={(e) => setCfg({ ...cfg, hikvision: { ...cfg.hikvision, password: e.target.value } })} className="bg-[#141414] border-white/10 text-white h-9 mt-1" /></div>
                  <div><Label className="text-[10px] uppercase tracking-widest text-gray-400">Canal</Label><Input value={cfg.hikvision.channel} onChange={(e) => setCfg({ ...cfg, hikvision: { ...cfg.hikvision, channel: e.target.value } })} placeholder="1" className="bg-[#141414] border-white/10 text-white h-9 mt-1" /></div>
                </div>
              </div>
            )}

            {cfg.provider === "synology" && (
              <div className="space-y-3 bg-[#0A0A0A] border border-white/5 p-3" data-testid={`${tid}-synology`}>
                <p className="text-[11px] text-gray-400">La conexión Synology global usa SYNOLOGY_QC/USER/PASS de variables de entorno. Solo indica el ID de cámara.</p>
                <div><Label className="text-[10px] uppercase tracking-widest text-gray-400">Camera ID</Label><Input value={cfg.synology.camera_id} onChange={(e) => setCfg({ ...cfg, synology: { ...cfg.synology, camera_id: e.target.value } })} placeholder="1" className="bg-[#141414] border-white/10 text-white h-9 mt-1" /></div>
              </div>
            )}

            {cfg.provider === "hls" && (
              <div className="space-y-3 bg-[#0A0A0A] border border-white/5 p-3" data-testid={`${tid}-hlscfg`}>
                <p className="text-[11px] text-gray-400">Pega tu URL .m3u8 directo (cualquier servidor HLS H.264).</p>
                <div><Label className="text-[10px] uppercase tracking-widest text-gray-400">URL HLS</Label><Input value={cfg.hls_url} onChange={(e) => setCfg({ ...cfg, hls_url: e.target.value })} placeholder="https://example.com/live/stream.m3u8" className="bg-[#141414] border-white/10 text-white h-9 mt-1" /></div>
              </div>
            )}

            <div className="flex justify-end gap-2 pt-2 border-t border-white/5">
              <Button variant="outline" onClick={() => setOpenCfg(false)} className="border-white/10 text-gray-400 uppercase text-xs">Cancelar</Button>
              <Button onClick={save} disabled={saving || !cfg.provider} className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase text-xs" data-testid={`${tid}-save-btn`}>
                {saving ? "Guardando…" : "Guardar"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
